import type { CreateEnquirySessionPayload } from 'api/accommodationBooking'
import { getUpgradePricingDiscount } from 'checkout/lib/utils/loyalty/propertyRateLoyaltyUpgrade'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { resolveChannelMarkupOrderIdentity, type ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import { isExperienceItem, isTransferItem } from 'lib/checkout/checkoutUtils'
import { floatify } from 'lib/maths/mathUtils'

type HotelPricedRate = Pick<App.BasePropertyRate, 'currency' | 'totals' | 'luxPlusTotals' | 'bundle' | 'paymentMethod' | 'token'> & {
  cheapestFlightPrice?: App.EnrichedPropertyRate['cheapestFlightPrice']
}

function getFlightOverheadPrice(rate: HotelPricedRate): number {
  if (typeof rate.cheapestFlightPrice === 'number') return rate.cheapestFlightPrice
  return 0
}

/**
 * Session sell/LP are hotel-only and exclusive of property fees. Mandatory bundle
 * tickets and flights stay off `sellTotal` so svc-order does not double-charge them.
 */
export function getHotelPricing(rate: HotelPricedRate): CreateEnquirySessionPayload['pricing'] {
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  const [hotelSellingPrice, hotelLuxPlusSellingPrice] = rate.bundle ? [
    rate.bundle.hotelSellingPrice,
    rate.bundle.hotelLuxPlusSellingPrice || undefined,
  ] : [
    floatify(rate.totals.sellingPrice - flightOverheadPrice),
    rate.luxPlusTotals?.sellingPrice
      ? floatify(rate.luxPlusTotals.sellingPrice - flightOverheadPrice)
      : undefined,
  ]

  const propertyFees = rate.totals.propertyFees || undefined
  // Collected by the property, like propertyFees, so it stays off sellTotal. Sent so the cart
  // can disclose it before a checkout enquiry has run and restamped the session.
  const surchargePayableAtProperty = rate.totals.surchargePayableAtProperty || undefined
  // Contract: luxPlusTotal is exclusive of property fees, matching sellTotal.
  const luxPlusTotal = hotelLuxPlusSellingPrice

  return {
    currency: rate.currency,
    sellTotal: hotelSellingPrice,
    ...(propertyFees != null ? { propertyFees } : {}),
    ...(surchargePayableAtProperty != null ? { surchargePayableAtProperty } : {}),
    ...(luxPlusTotal != null ? { luxPlusTotal } : {}),
    paymentMethod: rate.paymentMethod.type,
    ...(rate.token ? { priceToken: rate.token } : {}),
  }
}

/**
 * Session pricing at the un-marked base prices a channel markup claim carries. A member price with
 * no base is dropped rather than kept: it is the marked one, and svc-accommodation would take it
 * as the base.
 */
export function withBaseSessionPricing(
  pricing: CreateEnquirySessionPayload['pricing'],
  base: { baseSellTotal: number, baseLuxPlusTotal?: number },
): CreateEnquirySessionPayload['pricing'] {
  const { luxPlusTotal, ...publicPricing } = pricing
  return {
    ...publicPricing,
    sellTotal: base.baseSellTotal,
    ...(luxPlusTotal != null && base.baseLuxPlusTotal != null && { luxPlusTotal: base.baseLuxPlusTotal }),
  }
}

function mapOccupancy(occupancy: App.Occupants): CreateEnquirySessionPayload['occupancy'] {
  return {
    adults: occupancy.adults,
    ...(occupancy.children != null ? { children: occupancy.children } : {}),
    ...(occupancy.teenagers != null ? { teenagers: occupancy.teenagers } : {}),
    ...(occupancy.infants != null ? { infants: occupancy.infants } : {}),
    ...(occupancy.childrenAge?.length ? { childrenAges: occupancy.childrenAge } : {}),
  }
}

function mapSupplierMetaData(
  rate: App.EnrichedPropertyRate,
): CreateEnquirySessionPayload['supplierMetaData'] {
  if (rate.service.type === 'bedbank') {
    return {
      internalService: 'bedbank',
      selectedRate: {
        optionId: rate.optionId,
        internalRateId: rate.service.rateId,
        rateGroupId: rate.service.rateGroupId,
        bedGroupId: rate.service.bedGroups[0]?.id,
      },
      internalPropertyId: rate.service.propertyId,
      internalRoomId: rate.service.roomId,
    }
  }

  return {
    internalService: 'reservation',
    selectedRate: {
      optionId: rate.optionId,
      internalRateId: rate.service.rateId,
      ratePlanId: rate.service.ratePlanId,
      rateGroupId: rate.groupKey,
    },
    internalPropertyId: rate.service.propertyId,
    internalRoomId: rate.service.roomId,
  }
}

function mapSessionBundle(
  bundle: {
    bundleId: string
    tickets: Array<{ experienceId: string, productId: string, price: number }>
  },
  optIn?: true,
): NonNullable<CreateEnquirySessionPayload['bundle']> {
  return {
    bundleId: bundle.bundleId,
    tickets: bundle.tickets.map(({ experienceId, productId, price }) => ({
      experienceId,
      productId,
      ticketPrice: price,
    })),
    ...(optIn ? { optIn: true } : {}),
  }
}

function mapCartBundleTickets(
  item: App.Checkout.ExperienceItem | App.Checkout.TransferItem,
): App.Property.Bundle['tickets'] {
  let tickets: App.Property.Bundle['tickets']
  if (isTransferItem(item)) {
    const option = item.transfer.option
    if (!option) {
      throw new Error(`Missing bundled transfer option for cart item ${item.itemId}`)
    }
    tickets = [{ experienceId: item.experienceId, productId: option.id, price: option.price }]
  } else {
    tickets = item.tickets.map(ticket => ({
      experienceId: item.experienceId,
      productId: ticket.ticketId,
      price: ticket.price,
    }))
  }

  if (!tickets.length || tickets.some(ticket => !ticket.experienceId || !ticket.productId || !Number.isFinite(ticket.price))) {
    throw new Error(`Missing or invalid bundle ticket data for cart item ${item.itemId}`)
  }

  // V2 bundled children belong to one accommodation item, so prices need no allocation.
  return tickets
}

function mapCartItemBundles(
  item: App.Checkout.AccommodationV2Item,
  cartItems: ReadonlyArray<App.Checkout.AnyItem>,
): Pick<CreateEnquirySessionPayload, 'bundle' | 'optInBundles'> {
  const bundleIds = item.accommBundleIds
  if (!bundleIds?.length) return {}

  if (!item.bundleOptin && bundleIds.length > 1) {
    throw new Error(`Multiple mandatory bundles for cart item ${item.itemId}`)
  }

  const children = cartItems.filter((child): child is App.Checkout.ExperienceItem | App.Checkout.TransferItem => (
    (isExperienceItem(child) || isTransferItem(child)) && !!child.bundledItemIds?.includes(item.itemId)
  ))
  const bundles = bundleIds.map(bundleId => {
    const bundledChildren = children.filter(child => child.isAccomBundle === bundleId)
    if (!bundleId || !bundledChildren.length) {
      throw new Error(`Missing children for bundle ${bundleId} on cart item ${item.itemId}`)
    }
    if (bundledChildren.some(child => !!child.bundleOptin !== !!item.bundleOptin)) {
      throw new Error(`Conflicting opt-in flags for bundle ${bundleId} on cart item ${item.itemId}`)
    }

    return mapSessionBundle({
      bundleId,
      tickets: bundledChildren.flatMap(mapCartBundleTickets),
    }, item.bundleOptin ? true : undefined)
  })

  return item.bundleOptin ? { optInBundles: bundles } : { bundle: bundles[0] }
}

export interface BuildEnquirySessionPayloadParams {
  rate: App.EnrichedPropertyRate
  occupancy: App.Occupants
  checkIn: string
  checkOut: string
  region: string
  selectedOptionalBundles?: Array<Pick<App.Property.OptionalBundle, 'bundleId' | 'tickets'>>
  /** Shopped LuxPlus+ eligibility. Prices a loyalty upgrade's discount on the member basis. */
  isLuxPlusUser?: boolean
  /** Must match the `experiments.lppMemberPricingEnabled` the order is created with. */
  lppMemberPricingEnabled: boolean
  /** Loyalty package upgrade on this rate. Only an applied upgrade (booking the target) is stamped. */
  loyaltyUpgrade?: App.Checkout.PropertyRateLoyaltyUpgrade
  /** The channel markup source the rate was mapped with, to claim the markup inside its price. */
  markupSource?: ChannelMarkupSource
}

/**
 * A rate's pricing on the session's hotel-only basis, for a loyalty upgrade. Rate totals carry
 * mandatory bundle tickets and flights, which accomV2 charges as separate items.
 */
export function getHotelOnlyPropertyRatePricing(rate: HotelPricedRate): App.Checkout.PropertyRateLoyaltyUpgrade['sourcePricing'] {
  const { sellTotal, luxPlusTotal } = getHotelPricing(rate)
  const flightOverheadPrice = getFlightOverheadPrice(rate)
  // Bundle hotel prices are already un-marked; otherwise strip the flights off the un-marked total.
  const hotelBase = (totals: App.PropertyRateTotals, hotelSell: number) => rate.bundle
    ? hotelSell
    : floatify((totals.baseSellingPrice ?? totals.sellingPrice) - flightOverheadPrice)

  return {
    totals: { ...rate.totals, sellingPrice: sellTotal, baseSellingPrice: hotelBase(rate.totals, sellTotal) },
    ...(rate.luxPlusTotals && luxPlusTotal != null && {
      luxPlusTotals: { ...rate.luxPlusTotals, sellingPrice: luxPlusTotal, baseSellingPrice: hotelBase(rate.luxPlusTotals, luxPlusTotal) },
    }),
  }
}

/**
 * The channel markup claim for a session priced from `rate` by `getHotelPricing`, or `undefined`
 * when the hotel price it sends carries no markup.
 *
 * The campaign is resolved with the inputs `mapPropertyRate` marked the rate with (offer id and
 * type, and the lead pricing a v2 price band read), so the ids name the markup that priced it. The
 * public price's markup is claimed; a member-only markup is claimed on the member basis.
 * Public and member values are terms of that one matched markup, so both bases share its
 * `markupId`. `member` only changes the basis when the public value did not apply.
 *
 * A bundled rate's hotel price comes from `rate.bundle`, which is un-marked, so it never claims one.
 */
export function getEnquirySessionChannelMarkup(
  rate: HotelPricedRate & Pick<App.BasePropertyRate, 'service'>,
  markupSource: ChannelMarkupSource | undefined,
): App.Checkout.AccommodationV2ChannelMarkup | undefined {
  if (rate.bundle) return
  const isPublicMarked = !!rate.totals.channelMarkup
  const isMemberMarked = !!rate.luxPlusTotals?.channelMarkup
  if (!isPublicMarked && !isMemberMarked) return

  const [offerId, offerType] = rate.service.type === 'bedbank'
    ? [rate.service.propertyId, OFFER_TYPE_BED_BANK] as const
    : [rate.service.offerId, OFFER_TYPE_ALWAYS_ON] as const
  const identity = resolveChannelMarkupOrderIdentity(
    offerId,
    offerType,
    markupSource,
    rate.totals.channelMarkupLeadPricing,
    { member: !isPublicMarked },
  )
  if (!identity) return

  const { totals, luxPlusTotals } = getHotelOnlyPropertyRatePricing(rate)
  return {
    channelMarkupId: identity.channelMarkupId,
    ...(identity.version === 'v2' && { markupId: identity.markupId }),
    baseSellTotal: totals.baseSellingPrice,
    ...(luxPlusTotals && { baseLuxPlusTotal: luxPlusTotals.baseSellingPrice }),
  }
}

/**
 * A rate's session pricing with the channel markup claim that backs it. A marked rate whose markup
 * cannot be claimed (no campaign identity resolves, as when a v2 match has no `markupId` or the
 * source has drifted since the rate was mapped) is priced at its un-marked base: svc-accommodation
 * would otherwise take the marked price as the base, and svc-order would record no markup.
 */
export function getAccommodationV2SessionPricing(
  rate: App.EnrichedPropertyRate,
  markupSource: ChannelMarkupSource | undefined,
): { pricing: CreateEnquirySessionPayload['pricing'], channelMarkup?: App.Checkout.AccommodationV2ChannelMarkup } {
  const pricing = getHotelPricing(rate)
  const channelMarkup = getEnquirySessionChannelMarkup(rate, markupSource)
  if (channelMarkup) return { pricing, channelMarkup }

  const isMarked = !rate.bundle && (!!rate.totals.channelMarkup || !!rate.luxPlusTotals?.channelMarkup)
  if (!isMarked) return { pricing }

  const { totals, luxPlusTotals } = getHotelOnlyPropertyRatePricing(rate)
  return {
    pricing: withBaseSessionPricing(pricing, {
      baseSellTotal: totals.baseSellingPrice ?? pricing.sellTotal,
      ...(luxPlusTotals?.baseSellingPrice != null && { baseLuxPlusTotal: luxPlusTotals.baseSellingPrice }),
    }),
  }
}

/**
 * The applied upgrade's discount (target price minus source price) and its source rate. Mirrors the
 * legacy bedbank enquiry, which only sends the discount on the target rate's enquiry.
 */
export function buildSessionLoyaltyUpgrade(
  upgrade: App.Checkout.PropertyRateLoyaltyUpgrade | undefined,
  isLuxPlusUser: boolean | undefined,
  lppMemberPricingEnabled: boolean,
): Pick<CreateEnquirySessionPayload, 'loyaltyPackageUpgradeDiscount' | 'loyaltyUpgradeSource'> {
  if (!upgrade || upgrade.applied === false) return {}

  // On the basis svc-accommodation prices the session: member only with LPP member pricing on.
  const discount = getUpgradePricingDiscount(upgrade, !!isLuxPlusUser && lppMemberPricingEnabled)
  return {
    ...(discount && { loyaltyPackageUpgradeDiscount: discount }),
    loyaltyUpgradeSource: {
      serviceType: upgrade.source.serviceType,
      offerId: upgrade.source.serviceOfferId,
      propertyId: upgrade.source.propertyId,
      roomId: upgrade.source.roomId,
      roomRateId: upgrade.source.serviceRateId,
      packageId: upgrade.source.servicePackageId,
      sessionId: upgrade.sourceBooking?.sessionId,
    },
  }
}

/**
 * Builds a svc-accommodation enquiry-session create payload from a unified property rate.
 * Rate identity uses optionId + supplier internalRateId — never the ephemeral unified rateId.
 *
 * Mandatory `rate.bundle` and selected optional bundles are independent: both can sit on the
 * same session (`bundle` + `optInBundles`).
 */
export function buildEnquirySessionPayload({
  rate,
  occupancy,
  checkIn,
  checkOut,
  region,
  selectedOptionalBundles,
  isLuxPlusUser,
  lppMemberPricingEnabled,
  loyaltyUpgrade,
  markupSource,
}: BuildEnquirySessionPayloadParams): CreateEnquirySessionPayload {
  const mandatoryBundle = rate.bundle
  const { pricing, channelMarkup } = getAccommodationV2SessionPricing(rate, markupSource)

  return {
    pricing,
    property: { propertyId: rate.propertyId },
    room: { roomId: rate.roomId },
    occupancy: mapOccupancy(occupancy),
    supplierMetaData: mapSupplierMetaData(rate),
    checkIn,
    checkOut,
    region,
    currency: rate.currency,
    ...(mandatoryBundle && {
      bundle: mapSessionBundle(mandatoryBundle),
    }),
    ...(selectedOptionalBundles?.length && {
      optInBundles: selectedOptionalBundles.map(bundle => mapSessionBundle(bundle, true)),
    }),
    ...buildSessionLoyaltyUpgrade(loyaltyUpgrade, isLuxPlusUser, lppMemberPricingEnabled),
    ...(channelMarkup && { channelMarkup }),
  }
}

export function buildEnquirySessionPayloadFromCartItem(
  item: App.Checkout.AccommodationV2Item,
  region: string,
  isLuxPlusUser: boolean,
  cartItems: ReadonlyArray<App.Checkout.AnyItem>,
  lppMemberPricingEnabled: boolean,
): CreateEnquirySessionPayload {
  return {
    pricing: {
      currency: item.currency,
      sellTotal: item.sellTotal,
      ...(item.propertyFees != null ? { propertyFees: item.propertyFees } : {}),
      ...(item.surchargePayableAtProperty != null
        ? { surchargePayableAtProperty: item.surchargePayableAtProperty }
        : {}),
      ...(item.luxPlusTotal != null ? { luxPlusTotal: item.luxPlusTotal } : {}),
      paymentMethod: item.paymentMethod,
      ...(item.token ? { priceToken: item.token } : {}),
    },
    property: { propertyId: item.propertyId },
    room: { roomId: item.roomId },
    occupancy: mapOccupancy(item.occupancy),
    supplierMetaData: {
      internalService: item.internalService,
      selectedRate: {
        optionId: item.optionId,
        internalRateId: item.internalRateId,
        ...(item.rateGroupId ? { rateGroupId: item.rateGroupId } : {}),
        ...(item.bedGroupId ? { bedGroupId: item.bedGroupId } : {}),
        ...(item.ratePlanId ? { ratePlanId: item.ratePlanId } : {}),
      },
      internalPropertyId: item.internalPropertyId,
      internalRoomId: item.internalRoomId,
    },
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    region,
    currency: item.currency,
    ...mapCartItemBundles(item, cartItems),
    ...buildSessionLoyaltyUpgrade(item.loyaltyPropertyRateUpgrade, isLuxPlusUser, lppMemberPricingEnabled),
    ...(item.channelMarkup && { channelMarkup: item.channelMarkup }),
  }
}
